// The Reader (/read/:id) — the Reader paradigm (DECISIONS.md 2026-07-05;
// ladder flattened 2026-07-26): a document rendered as text, with the graph
// brought TO it. Depth 0 = just read. Depth 1 = anchored claims underlined
// by lens state; click one for its live summary + a door to the standing
// argument. Depth 2 = each claim with human signal opens its full case
// INLINE under its paragraph — summary head + a horizontal carousel of
// cards (lens glass box first, then votable edges); suggestion-only claims
// wait behind a quiet chip. The old margin column (counter notes at 2, full
// panels at 3) is gone with the ≥1100px layout fork it required.
//
// Classification honors the invariants: unvoted claims render as a quiet
// dotted "no judgments yet" (an affordance, never a score); disagreement
// (lens spread / conflict) renders as contested, never averaged away.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import { useTheme } from './theme';
import EdgeSentence from './EdgeSentence';
import TrustStance from './TrustStance';
import ReadTable from './ReadTable';
import CaptureClaim, { placeFromSelection } from './CaptureClaim';
import Nav from './Nav';
import Byline from './Byline';
import { Explain } from './Explain';
import './css/read.css';

function classify(claim) {
  if (!claim.has_signal) return 'quiet';
  if (claim.conflict) return 'contested';
  const adv = claim.scores?.adversarial_consensus;
  if (typeof adv === 'number' && adv >= 0.3) return 'survives';
  return 'mixed';
}

function spreadOf(scores) {
  const vals = Object.values(scores || {}).filter((v) => typeof v === 'number');
  if (vals.length < 2) return 0;
  return Math.max(...vals) - Math.min(...vals);
}

const STATE_LABEL = {
  survives: 'survives division',
  contested: 'contested',
  mixed: 'mixed signal',
  quiet: 'no judgments yet',
};

const rdSnip = (s, n = 90) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');

// Voted-equivalence provenance (restates-cluster collapse, Argument.js
// precedent): the claim's cluster-mates as a one-line note, and a badge on
// edges the backend re-pointed to a cluster canonical.
function RdClusterNote({ cluster, claimId }) {
  if (!cluster) return null;
  const others = cluster.members.filter((m) => m.id !== claimId);
  if (others.length === 0) return null;
  return (
    <p className="rd-pop-row rd-cluster-note">
      <Explain concept="restates-cluster">≈ {cluster.members.length} statements collapsed</Explain> — also stated as{' '}
      {others.slice(0, 2).map((m, i) => (
        <span key={m.id}>
          {i > 0 && ' · '}
          <Link to={`/object/${m.id}`} style={{ color: 'inherit' }}>“{rdSnip(m.content)}”</Link>
        </span>
      ))}
      {others.length > 2 && ` · +${others.length - 2} more`}
    </p>
  );
}

function RdViaBadge({ edge }) {
  if (!edge.via_restates) return null;
  const orig = edge.via_restates.source?.content || edge.via_restates.target?.content || '';
  return (
    <Explain concept="restates-cluster">
      <span className="rd-via" title={`originally on the restatement: “${rdSnip(orig, 160)}”`}>
        via restatement
      </span>
    </Explain>
  );
}

// Split the document into paragraphs (global offsets preserved) and cut each
// around the claim anchors that fall inside it. Overlapping anchors keep the
// earliest; anchors crossing a paragraph boundary are clipped to it.
function buildParagraphs(content, claims) {
  const anchors = claims
    .filter((c) => c.start_offset !== null && c.end_offset !== null && c.end_offset > c.start_offset)
    .sort((a, b) => a.start_offset - b.start_offset);
  const clean = [];
  let cursor = 0;
  for (const a of anchors) {
    if (a.start_offset >= cursor) {
      clean.push(a);
      cursor = a.end_offset;
    }
  }

  const paras = [];
  const re = /\n{2,}/g;
  let last = 0;
  let m;
  while ((m = re.exec(content))) {
    paras.push({ start: last, end: m.index });
    last = re.lastIndex;
  }
  paras.push({ start: last, end: content.length });

  return paras
    .map((p) => {
      const inP = clean.filter((a) => a.start_offset < p.end && a.end_offset > p.start);
      const segs = [];
      let pos = p.start;
      for (const a of inP) {
        const s = Math.max(a.start_offset, p.start);
        const e = Math.min(a.end_offset, p.end);
        if (s > pos) segs.push({ text: content.slice(pos, s) });
        segs.push({ text: content.slice(s, e), claim: a });
        pos = e;
      }
      if (pos < p.end) segs.push({ text: content.slice(pos, p.end) });
      return segs;
    })
    .filter((segs) => segs.length && segs.some((s) => s.text.trim()));
}

function ClaimPopover({ pop, onClose, user, onVoted }) {
  // sheet = phone mode: the popover has no margin to float into, so it rises
  // from the bottom edge instead (2026-07-25 mobile pass); a backdrop dims the
  // article and an ✕ closes without hunting for empty space to tap.
  const { claim, x, y, maxH, sheet } = pop;
  const state = classify(claim);
  const votes = claim.up_votes + claim.down_votes;

  return (
    <>
      {sheet && <div className="rd-pop-backdrop" onClick={onClose} />}
    <div
      className={`rd-pop${sheet ? ' rd-pop--sheet' : ''}`}
      style={sheet ? undefined : { left: x, top: y, maxHeight: maxH }}
      onClick={(e) => e.stopPropagation()}
    >
      {sheet && (
        <button className="rd-pop-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      )}
      <Explain concept="claim-state"><span className={`rd-pop-state s-${state}`}>{STATE_LABEL[state]}</span></Explain>
      <p className="rd-pop-claim">{claim.content}</p>
      <RdClusterNote cluster={claim.restates_cluster} claimId={claim.id} />

      {claim.has_signal ? (
        <>
          <RdBand claim={claim} />
          <p className="rd-pop-row">
            <Explain concept="voting">
              <b>{votes}</b> judgment{votes === 1 ? '' : 's'} across its edges · <b>{claim.support_count}</b>{' '}
              supporting · <b>{claim.contradict_count}</b> contradicting
              {claim.qualify_count > 0 && (
                <> · <b>{claim.qualify_count}</b> qualifying</>
              )}
            </Explain>
          </p>
          {claim.tension?.top?.sentence && (
            <p className="rd-pop-row"><Explain concept="tension">{claim.tension.top.sentence}</Explain></p>
          )}
        </>
      ) : (
        <p className="rd-pop-row">
          <Explain concept="activation">No one has judged this claim's edges yet.</Explain>
        </p>
      )}

      {claim.counter && (
        <div className="rd-pop-counter">
          <Explain concept="standing-counter">Strongest standing counter:</Explain> “{claim.counter.content}”
        </div>
      )}

      {/* One-click trust stake (vector-trust phase 2): reachable where
          reading happens — the stake lands on the claim's SOCIAL axis,
          beside the evidence, never inside the band. */}
      {user && (
        <div className="rd-pop-row" style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-3)', flexWrap: 'wrap' }}>
          <TrustStance targetId={claim.id} kind="claim" compact onChanged={onVoted} />
          {claim.axes?.find((a) => a.axis === 'trusts') && (() => {
            const t = claim.axes.find((a) => a.axis === 'trusts');
            return (
              <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
                social axis: {t.net > 0 ? '+' : ''}{t.net} ({t.count === 1 ? '1 stake' : `${t.count} stakes`})
              </span>
            );
          })()}
        </div>
      )}

      {claim.edges?.length > 0 && (
        <div className="rd-pop-edges">
          <h4><Explain concept="voting">Judge its edges</Explain></h4>
          {claim.edges.map((e) => (
            <div key={e.id}>
              <EdgeSentence edge={e} focusId={claim.id} user={user} onVoted={onVoted} onRetyped={onVoted} />
              <RdViaBadge edge={e} />
            </div>
          ))}
          {!user && <p className="rd-pop-row">Log in to vote.</p>}
        </div>
      )}

      <div className="rd-doors">
        <Link className="rd-open" to={`/object/${claim.id}`} onClick={onClose}>
          Open the standing argument →
        </Link>
        {claim.edges?.length > 0 && (
          <Link className="rd-open" to={`/argument/${claim.id}`} onClick={onClose}>
            Walk the argument →
          </Link>
        )}
      </div>
    </div>
    </>
  );
}

// The trust band: marker = the viewer's weighted blend, width = how much the
// lenses disagree. One renderer for the popover and the case block.
function RdBand({ claim }) {
  const spread = spreadOf(claim.scores);
  const marker = ((claim.combined + 1) / 2) * 100;
  const lo = ((Math.max(-1, claim.combined - spread / 2) + 1) / 2) * 100;
  const width = Math.min(100 - lo, (spread / 2) * 100);
  return (
    <>
      <div className="rd-band">
        <span className="rd-band-spread" style={{ left: `${lo}%`, width: `${width}%` }} />
        <span className="rd-band-marker" style={{ left: `${marker}%` }} />
      </div>
      <div className="rd-band-label">
        <span>−1</span>
        <span><Explain concept="trust-band" /></span>
        <span>+1</span>
      </div>
    </>
  );
}

// New depth 2 (ruling 2026-07-26): the full standing argument, inline under
// its paragraph. Summary head (state, tension, cluster, compressed band),
// then the case as a horizontal carousel — the lens glass box is the first
// card (every lens separately; the mean is never the headline), every edge
// a votable card via the shared renderer.
function CaseBlock({ claim, lenses, user, onVoted }) {
  const state = classify(claim);
  const lensName = (id) => lenses.find((l) => l.id === id)?.name || id.replace(/_/g, ' ');
  const lensDesc = (id) => lenses.find((l) => l.id === id)?.description;
  const votes = claim.up_votes + claim.down_votes;
  return (
    <div className="rd-case">
      <div className="rd-case-head">
        <Explain concept="claim-state"><span className={`rd-pop-state s-${state}`}>{STATE_LABEL[state]}</span></Explain>
        <Link className="rd-case-subject" to={`/object/${claim.id}`}>
          “{rdSnip(claim.content, 150)}”
        </Link>
        {claim.tension?.top?.sentence && (
          <p className="rd-case-tension"><Explain concept="tension">{claim.tension.top.sentence}</Explain></p>
        )}
        <RdClusterNote cluster={claim.restates_cluster} claimId={claim.id} />
        <RdBand claim={claim} />
        <p className="rd-case-votes">
          <Explain concept="voting">
            <b>{votes}</b> judgment{votes === 1 ? '' : 's'} · <b>{claim.support_count}</b> supporting ·{' '}
            <b>{claim.contradict_count}</b> contradicting
            {claim.qualify_count > 0 && (
              <> · <b>{claim.qualify_count}</b> qualifying</>
            )}
          </Explain>
        </p>
      </div>
      <div className="rd-carousel">
        <div className="rd-card rd-card--lenses">
          {Object.entries(claim.scores || {}).map(([lid, v]) =>
            v === 0 ? (
              // no-signal renders as absence, never 0 (TrustInspect's — precedent):
              // through this lens the claim is silent, not scored at the center
              <div className="rd-lens rd-lens--quiet" key={lid} title={lensName(lid)}>
                <span className="rd-lens-name">
                  <Explain title={lensName(lid)} text={lensDesc(lid)} graphKey={`lens:${lid}`}>
                    {lensName(lid)}
                  </Explain>
                </span>
                <span className="rd-lens-none"><Explain concept="no-signal">—</Explain></span>
                <span className="rd-lens-val" />
              </div>
            ) : (
              <div className="rd-lens" key={lid} title={`${lensName(lid)}: ${v.toFixed(2)}`}>
                <span className="rd-lens-name">
                  <Explain title={lensName(lid)} text={lensDesc(lid)} graphKey={`lens:${lid}`}>
                    {lensName(lid)}
                  </Explain>
                </span>
                <span className="rd-lens-bar">
                  <i style={{ left: `${((Math.max(-1, Math.min(1, v)) + 1) / 2) * 100}%` }} />
                </span>
                <span className="rd-lens-val">{v.toFixed(2)}</span>
              </div>
            )
          )}
        </div>
        {claim.edges?.map((e) => (
          <div className="rd-card" key={e.id}>
            <EdgeSentence edge={e} focusId={claim.id} user={user} onVoted={onVoted} onRetyped={onVoted} />
            <RdViaBadge edge={e} />
          </div>
        ))}
      </div>
      <div className="rd-doors">
        <Link className="rd-open" to={`/object/${claim.id}`}>
          Open the standing argument →
        </Link>
        {claim.edges?.length > 0 && (
          <Link className="rd-open" to={`/argument/${claim.id}`}>
            Walk the argument →
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Read() {
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // ?d=0..2 sets the initial depth — a reading link can share its focus.
  // Old ?d=3 links clamp to 2 (ruling 2026-07-26: no rerouting, pre-traffic)
  const [depth, setDepth] = useState(() => {
    const d = parseInt(searchParams.get('d') ?? '1', 10);
    return Number.isInteger(d) ? Math.max(0, Math.min(2, d)) : 1;
  });
  const [pop, setPop] = useState(null);
  // The hand capture gesture (Slice A). Separate state from `pop` on purpose:
  // a claim popover explains an EXISTING claim, the capture modal makes a new
  // one, and letting them share a slot means one can silently replace the other
  // mid-gesture.
  const [capture, setCapture] = useState(null);
  // the theme is global now (theme.js, same 'wv-theme' key this view started);
  // read.css keeps its scoped skin keyed off data-theme on rd-root
  const { theme } = useTheme();
  const [extracting, setExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState(null);
  const articleRef = useRef(null);
  const captureGuard = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/read/${id}`);
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to load');
      const body = await res.json();
      setData(body);
      // keep an open popover in sync after a vote refetch
      setPop((p) => {
        if (!p) return p;
        const fresh = body.claims.find((c) => c.id === p.claim.id);
        return fresh ? { ...p, claim: fresh } : p;
      });
      return body;
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // close popover on outside click / Escape; 0–1 set depth
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPop(null);
        setCapture(null);
      }
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === '0') setDepth(0);
      if (e.key === '1') setDepth(1);
      if (e.key === '2') setDepth(2);
    };
    // The drag that ENDS a text selection also fires a click on document, which
    // would close the capture modal in the same tick it opened. The mouseup
    // handler sets this guard and the very next click spends it.
    const onClick = () => {
      setPop(null);
      if (captureGuard.current) {
        captureGuard.current = false;
        return;
      }
      setCapture(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
    };
  }, []);

  // The selection gesture itself. Bound to the article element rather than the
  // document so selecting nav text or a popover's own contents does nothing.
  const onArticleMouseUp = useCallback(() => {
    if (!user) return; // signed-out readers read; the door needs an author
    // setTimeout 0: on mouseup the selection is not yet settled in every engine.
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      // Same threshold the server enforces, so the client never offers a
      // gesture the door will refuse.
      if (text.length < 8) return;
      const range = sel.getRangeAt(0);
      if (!articleRef.current || !articleRef.current.contains(range.commonAncestorContainer)) return;
      captureGuard.current = true;
      setCapture({ quote: text, ...placeFromSelection(sel) });
    }, 0);
  }, [user]);

  const paragraphs = useMemo(
    () => (data ? buildParagraphs(data.document.content, data.claims) : []),
    [data]
  );
  const unanchored = useMemo(
    () => (data ? data.claims.filter((c) => c.start_offset === null) : []),
    [data]
  );

  // The workflow unlock: a document with no claims offers extraction right
  // here (same decompose endpoint as the object page; idempotent, gate-aware).
  const extractClaims = async () => {
    setExtracting(true);
    setExtractNote(null);
    try {
      const res = await apiFetch(`/api/objects/${id}/decompose`, { method: 'POST' });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Extraction failed');
      await load();
      if (!body.existing && (body.created ?? 0) === 0 && (body.reused ?? 0) === 0) {
        setExtractNote('No claims could be extracted from this text.');
      }
    } catch (err) {
      setExtractNote(err.message);
    } finally {
      setExtracting(false);
    }
  };

  const openPop = useCallback((e, claim) => {
    e.stopPropagation();
    // phones: no margin to float into — the popover becomes a bottom sheet
    if (window.matchMedia('(max-width: 640px)').matches) {
      setPop({ claim, sheet: true });
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    // keep the popover on screen: its CSS width is min(420px, 100vw - 24px),
    // so clamp left into [12, innerWidth - 432], and cap its height to the
    // space below the chosen top so it never runs off the bottom edge
    const y = Math.min(rect.bottom + 8, window.innerHeight - 280);
    setPop({
      claim,
      x: Math.max(12, Math.min(rect.left, window.innerWidth - 432)),
      y,
      maxH: window.innerHeight - y - 16,
    });
  }, []);

  if (error) {
    return (
      <div className="rd-root" data-theme={theme}>
        <div className="rd-status">
          {error} — <Link to="/" style={{ color: 'inherit' }}>back home</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rd-root" data-theme={theme}>
        <div className="rd-status">Loading…</div>
      </div>
    );
  }

  const [titleSegs, ...bodyParas] = paragraphs;
  const title = titleSegs ? titleSegs.map((s) => s.text).join('') : 'Untitled';

  const renderSegs = (segs, key) => (
    <p key={key}>
      {segs.map((s, i) =>
        s.claim ? (
          <span
            key={i}
            className={`rd-claim rd-claim--${classify(s.claim)}`}
            onClick={depth >= 1 ? (e) => openPop(e, s.claim) : undefined}
            role={depth >= 1 ? 'button' : undefined}
            tabIndex={depth >= 1 ? 0 : undefined}
            onKeyDown={
              depth >= 1
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') openPop(e, s.claim);
                  }
                : undefined
            }
          >
            {s.text}
          </span>
        ) : (
          <span key={i}>{s.text}</span>
        )
      )}
    </p>
  );

  return (
    <div
      className={`rd-root ${depth >= 1 ? 'rd-d1' : ''} ${depth >= 2 ? 'rd-d2' : ''}`}
      data-theme={theme}
    >
      {/* The global nav rides here too (owner decision 2026-07-05); read.css
          re-inks it through the --nav-* vars, following the Reader's theme. */}
      <Nav />
      <header className="rd-bar">
        <div className="rd-byline">
          {/* The shared attribution renderer (2026-08-14). This strip used to
              map `byline` inline with NO empty state, so a document whose
              author extraction failed — which lib/webExtract.ts does routinely,
              it reads two meta keys — rendered a blank bar. It now says
              "unattributed", the floor every surface shares. The arrival chips
              below stay here: they describe how the text GOT here, not who
              said it, so they are not attribution slots. */}
          <Byline attribution={{ authored_by: data.byline }} variant="chip" />
          {data.document.arrival === 'model_relay' && (
            <span className="rd-chip" style={{ opacity: 0.75 }}>
              <Explain concept="arrival-path">⇢ relayed{data.document.arrival_via ? ` by ${data.document.arrival_via}` : ''} — not fetched</Explain>
            </span>
          )}
          {data.document.arrival === 'archive' && (
            <span className="rd-chip" style={{ opacity: 0.75 }}>
              <Explain concept="arrival-path">⇢ via archive snapshot</Explain>
            </span>
          )}
        </div>
        <span className="rd-spacer" />
        <Link className="rd-chip" to={`/object/${id}`} style={{ textDecoration: 'none' }}>
          object page →
        </Link>
        <span className="rd-count">
          <Explain concept="decomposition">
            {data.anchored_count} claim{data.anchored_count === 1 ? '' : 's'} anchored
            {unanchored.length > 0 ? ` · ${unanchored.length} not locatable` : ''}
          </Explain>
        </span>
        <div className="rd-depth" role="group" aria-label="Reading depth">
          {[0, 1, 2].map((d) => (
            <button
              key={d}
              className={depth === d ? 'on' : ''}
              title={
                d === 0 ? 'just read'
                : d === 1 ? 'claims underlined'
                : 'each judged claim opens its case beneath its paragraph'
              }
              onClick={() => setDepth(d)}
            >
              {d}
            </button>
          ))}
          <Explain concept="reading-depth" />
        </div>
      </header>

      <main className="rd-page" ref={articleRef} onMouseUp={onArticleMouseUp}>
        <h1>{title}</h1>
        <div className="rd-meta">
          {data.document.asserted_at && (
            <span>{new Date(data.document.asserted_at).toLocaleDateString()}</span>
          )}
          <span>{data.claims.length} claims decomposed</span>
        </div>

        {data.claims.length === 0 && (
          <div className="rd-extract">
            {data.document.model_access !== 'sealed' ? (
              user ? (
                <>
                  <button onClick={extractClaims} disabled={extracting}>
                    {extracting ? 'Reading the article…' : '✦ Extract claims'}
                  </button>
                  <span>
                    <Explain concept="decomposition">
                      AI decomposes the text into atomic claims, underlined where they're made.
                    </Explain>
                  </span>
                </>
              ) : (
                <span>
                  No claims extracted yet — <Link to="/login">log in</Link> to extract them.
                </span>
              )
            ) : (
              <span>
                <Explain concept="model-visibility">
                  This document is sealed — its text never reaches a model, so claims can't be
                  auto-extracted.
                </Explain>{' '}
                {/* Was a pointer to the object page's weaker gesture, which
                    Slice A deleted. The hand path now lives here, which is
                    also the surface where the sealed case matters most. */}
                {user ? 'Select any sentence below to mint a claim by hand.' : (
                  <>
                    <Link to={`/login?from=/read/${id}`}>Log in</Link> to mint claims by hand.
                  </>
                )}
              </span>
            )}
            {extractNote && <span className="rd-extract-note">{extractNote}</span>}
          </div>
        )}

        <div className="rd-legend" aria-hidden={depth < 1}>
          <span><i className="l-s">survives division</i></span>
          <span><i className="l-c">contested</i></span>
          <span><i className="l-m">mixed signal</i></span>
          <span><i className="l-q">no judgments yet</i></span>
          <Explain concept="claim-state" />
        </div>

        {bodyParas.map((segs, i) => {
          // depth 2: every claim in this paragraph with human signal (an
          // activated edge or a standing counter) opens its full case inline
          // below the paragraph, in reading order; suggestion-only claims
          // wait behind a quiet chip (activation doctrine: unvoted edges are
          // scaffolding, reachable but never ahead of the text)
          const cased = [];
          const quiet = [];
          const seen = new Set();
          if (depth >= 2) {
            for (const s of segs) {
              if (!s.claim || seen.has(s.claim.id)) continue;
              seen.add(s.claim.id);
              if (!s.claim.edges?.length) continue;
              (s.claim.has_signal || s.claim.counter ? cased : quiet).push(s.claim);
            }
          }
          return (
            <div className="rd-row" key={i}>
              {renderSegs(segs, 'p')}
              {cased.map((c) => (
                <CaseBlock key={c.id} claim={c} lenses={data.lenses} user={user} onVoted={load} />
              ))}
              {quiet.length > 0 && (
                <div className="rd-sugg-row">
                  {quiet.map((c) => (
                    <button key={c.id} className="rd-sugg" onClick={(e) => openPop(e, c)}>
                      “{rdSnip(c.content, 48)}” — {c.edges.length} unjudged suggestion{c.edges.length === 1 ? '' : 's'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <ReadTable documentId={id} user={user} onGraphChange={load} />

        {unanchored.length > 0 && (
          <section className="rd-unanchored">
            <h2><Explain concept="decomposition">From this article, not locatable in its text</Explain></h2>
            {unanchored.map((c) => (
              <button key={c.id} onClick={(e) => openPop(e, c)}>
                {c.content}
              </button>
            ))}
          </section>
        )}
      </main>

      {pop && <ClaimPopover pop={pop} onClose={() => setPop(null)} user={user} onVoted={load} />}
      {capture && (
        <CaptureClaim
          sourceId={id}
          quote={capture.quote}
          sheet={capture.sheet}
          position={capture.position}
          modelAccess={data?.document?.model_access}
          // refetch so the new claim's underline appears without a reload —
          // the anchor was located server-side, so the span is already known.
          // The selection clears HERE, after a successful mint, where the
          // fresh underline replaces it — and only here (2026-08-11): cancel
          // and click-away must leave the reader's selection alone, because
          // the popover opens on every ≥8-char selection and wiping the
          // range made an ordinary select-to-copy impossible.
          onDone={() => {
            window.getSelection()?.removeAllRanges();
            load();
          }}
          onClose={() => setCapture(null)}
        />
      )}
    </div>
  );
}
